/**
 * Felix Scroll Progress — <felix-scroll-progress>
 *
 * Web component that drives --scroll-progress (0–1) on itself via LERP.
 * CSS custom properties inherit down to all children.
 *
 * Attributes:
 *   mode="top"        Default. Progress goes 0→1 over <threshold> px after
 *                     the element's top edge reaches the viewport top.
 *                     Ideal for "animate as element exits up" interactions.
 *
 *   mode="fill"       Progress fills the entire scroll journey through the
 *                     element. 0 when element top reaches viewport top, 1
 *                     when element bottom reaches viewport bottom.
 *                     Ideal for sticky scroll-pin sections — pair with a
 *                     tall element (e.g. height: 200vh) and a sticky child.
 *
 *   threshold="400"   Scroll distance (px) over which progress goes 0→1.
 *                     Only used in mode="top". Default: 400.
 *
 *   desktop-only      Restrict animation to desktop (≥ 1024px).
 *                     On mobile --scroll-progress stays at 0.
 *
 * @package Felix
 */

class FelixScrollProgress extends HTMLElement {
	connectedCallback() {
		if (document.body.classList.contains('block-editor-page')) return
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.style.setProperty('--scroll-progress', '1')
			return
		}

		const mode = this.getAttribute('mode') || 'top'
		const threshold = parseInt(this.getAttribute('threshold')) || 400
		const desktopOnly = this.hasAttribute('desktop-only')

		if (desktopOnly && !window.matchMedia('(min-width: 64rem)').matches) return

		let current = 0
		let target = 0
		let raf = null
		const LERP = 0.07

		const computeTarget = () => {
			const rect = this.getBoundingClientRect()
			if (mode === 'fill') {
				const scrollable = this.offsetHeight - window.innerHeight
				return scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0
			}
			return Math.min(1, Math.max(0, -rect.top / threshold))
		}

		const tick = () => {
			current += (target - current) * LERP
			if (Math.abs(target - current) < 0.0005) {
				current = target
				raf = null
			} else {
				raf = requestAnimationFrame(tick)
			}
			this.style.setProperty('--scroll-progress', current.toFixed(4))
		}

		const onScroll = () => {
			target = computeTarget()
			if (!raf) raf = requestAnimationFrame(tick)
		}

		window.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('resize', onScroll, { passive: true })

		// Set correct initial state synchronously (no lerp on load)
		target = computeTarget()
		current = target
		this.style.setProperty('--scroll-progress', current.toFixed(4))

		this._cleanup = () => {
			window.removeEventListener('scroll', onScroll)
			window.removeEventListener('resize', onScroll)
			if (raf) cancelAnimationFrame(raf)
		}
	}

	disconnectedCallback() {
		this._cleanup?.()
	}
}

if (!customElements.get('felix-scroll-progress')) {
	customElements.define('felix-scroll-progress', FelixScrollProgress)
}
